// Shared by the Fulfillment and Deliveries summaries: one row per product on the
// order, plus one row per product-group line of the contract.
//
// A truck can ship a different product of the same group than the contract line
// names (terminalas links it to that line). Reservation and delivery items carry
// the line they were booked against (`orderLineType` + `orderLineId`), so they
// are counted on that line's row, not on the row of the product that physically
// shipped.

export interface OrderProductGroupLine {
  id: string;
  productGroup?: { id: string; name?: string; code?: string | null } | null;
  quantity?: number;
}

export interface SummaryRowBase {
  // Row identity: the product id, or `group:<line id>` for a group line.
  key: string;
  // Product to pre-select in "reserve" / "ship"; empty for a group line,
  // which has no single product.
  productId: string;
  productTitle: string;
  sku: string;
  // Set only on a group line's row.
  group: { code: string; name: string } | null;
  ordered: number;
}

// Where an item points: the order line it was booked against.
interface LineRef {
  orderLineType?: string | null;
  orderLineId?: string | null;
}

const lineRefKey = (type: string | null | undefined, id: string) =>
  `${type === "orderProductGroup" ? "orderProductGroup" : "orderItem"}:${id}`;

// A row for the product of a reservation or delivery item (sent by the API as
// a whole object or as just its id), for products that are on no order line.
function productRowOf(ref: unknown): SummaryRowBase {
  const product = typeof ref === "object" && ref !== null ? (ref as any) : null;
  const productId = product?.id || (typeof ref === "string" ? ref : "");
  return {
    key: productId,
    productId,
    productTitle: product?.title || "-",
    sku: product?.sku || "-",
    group: null,
    ordered: 0,
  };
}

// Builds the rows that exist before any reservation or delivery is counted
// (items first, then group lines), and a map from each order line to its row.
export function orderLineRows(
  orderItems: any[],
  productGroups: OrderProductGroupLine[]
): { rows: SummaryRowBase[]; rowKeyByLine: Map<string, string> } {
  const rows: Record<string, SummaryRowBase> = {};
  const rowKeyByLine = new Map<string, string>();

  orderItems.forEach((item: any) => {
    const productRef = item.product;
    const key =
      typeof productRef === "object" && productRef !== null
        ? productRef.id
        : productRef || item.id;

    if (!key) return;

    rowKeyByLine.set(lineRefKey("orderItem", item.id), key);
    if (rows[key]) {
      rows[key].ordered += item.quantity || 0;
      return;
    }
    rows[key] = {
      key,
      productId: key,
      productTitle:
        item.productTitle ||
        (typeof productRef === "object" ? productRef?.title : null) ||
        "-",
      sku:
        item.sku ||
        (typeof productRef === "object" ? productRef?.sku : null) ||
        "-",
      group: null,
      ordered: item.quantity || 0,
    };
  });

  productGroups.forEach((line) => {
    const key = `group:${line.id}`;
    const name = line.productGroup?.name || "-";
    rowKeyByLine.set(lineRefKey("orderProductGroup", line.id), key);
    rows[key] = {
      key,
      productId: "",
      productTitle: name,
      sku: "-",
      group: { code: line.productGroup?.code || "", name },
      ordered: line.quantity || 0,
    };
  });

  return { rows: Object.values(rows), rowKeyByLine };
}

// Where a reservation or delivery item counts: the row of the order line it was
// booked against; an item without a known line (older rows) counts on its own
// product's row. `fallbackRow` is that product's row, for products that are on
// no order line and so have no row yet.
export function rowOfItem(
  item: LineRef & { product?: unknown },
  rowKeyByLine: Map<string, string>
): { rowKey: string; fallbackRow: SummaryRowBase } {
  const fallbackRow = productRowOf(item.product);
  if (item.orderLineId) {
    const lineRowKey = rowKeyByLine.get(
      lineRefKey(item.orderLineType, item.orderLineId)
    );
    if (lineRowKey) return { rowKey: lineRowKey, fallbackRow };
  }
  return { rowKey: fallbackRow.productId, fallbackRow };
}

// Row title for display: a group line reads "<name> (group <code>)".
export function summaryRowTitle(
  row: SummaryRowBase,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (!row.group?.code) return row.productTitle;
  return t("fulfillment_group_line", {
    name: row.group.name,
    code: row.group.code,
  });
}
